import { useEffect } from "react";
import PropTypes from "prop-types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CopyField from "./CopyField";

/**
 * The username and password an agent signs in with, shown once.
 *
 * Same shape as MintAgentModal's token screen: no close except "I've saved
 * it", and the parent drops the credential from state on `onExited`, so
 * reopening for anything else cannot show it again.
 */
function AgentLoginModal({
  show,
  onHide,
  onExited,
  credentials = null,
  returnFocusTo = null,
}) {
  // Belt: whenever this isn't shown, make sure the parent has forgotten the
  // credential — covers a caller that only flips `show` without going
  // through `onHide` (the old modal's onExited fired the same forgetting once
  // the modal's fade-out finished; the parent's onHide already clears its
  // own state synchronously, so this only ever repeats that call).
  useEffect(() => {
    if (!show) onExited();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <Dialog open={show} onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        // The #28 shape: bounded height, only the body scrolls — on a phone
        // an unbounded dialog put "I've saved it" below the fold with no way
        // to reach it (#34 review B).
        className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => {
          if (returnFocusTo?.current) {
            event.preventDefault();
            returnFocusTo.current.focus();
          }
        }}
      >
        {credentials && (
          <>
            <DialogHeader className="border-b px-6 py-4 text-left">
              <DialogTitle className="font-display text-xl">
                {credentials.reset
                  ? `New password for ${credentials.username}`
                  : `${credentials.username} can log in now`}
              </DialogTitle>
              <DialogDescription className="sr-only">
                The agent&apos;s username and password, shown once.
              </DialogDescription>
            </DialogHeader>
            <div
              data-slot="dialog-body"
              className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
            >
              <Alert>
                <AlertDescription>
                  <strong>This is the only time the password is shown.</strong>{" "}
                  Copy it now; if it&apos;s lost, reset the login for a new one.
                  {credentials.reset
                    ? " Every session this agent had open has already stopped working."
                    : ""}
                </AlertDescription>
              </Alert>
              <CopyField
                id="agent-login-username"
                label="Username"
                value={credentials.username}
              />
              <CopyField
                id="agent-login-password"
                label="Password"
                value={credentials.password}
              />
              <p className="mb-2 text-sm text-muted-foreground">
                The agent signs in at <code>/login</code> like a person and
                stays signed in, logging itself back in when the session lapses.
                Every task it touches is recorded as{" "}
                <strong>{credentials.username}</strong>, and the board shows a
                banner saying an agent is driving.
              </p>
              <Alert>
                <AlertDescription>
                  Give it its <strong>own browser profile</strong>, holding{" "}
                  <strong>no human credentials</strong> — a computer-use agent
                  facing a login form will fill it from whatever the
                  profile&apos;s password manager offers, and a borrowed human
                  session is exactly what this is meant to replace.
                </AlertDescription>
              </Alert>
            </div>
            <DialogFooter className="border-t px-6 py-4">
              <Button onClick={onHide}>I&apos;ve saved it</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

AgentLoginModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onExited: PropTypes.func.isRequired,
  returnFocusTo: PropTypes.shape({ current: PropTypes.any }),
  credentials: PropTypes.shape({
    username: PropTypes.string.isRequired,
    password: PropTypes.string.isRequired,
    reset: PropTypes.bool,
  }),
};

export default AgentLoginModal;
